import type { Translations } from "./sl";

export const en: Translations = {
  common: {
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    next: "Next",
    confirm: "Confirm",
    send: "Send",
    sending: "Sending...",
    search: "Search",
    noData: "No data",
    error: "Error",
    success: "Success",
    close: "Close",
    yes: "Yes",
    no: "No",
    or: "or",
    and: "and",
    all: "All",
    none: "None",
    previous: "Previous",
    nextPage: "Next",
    page: "Page",
    of: "of",
    justNow: "just now",
    minutesAgo: "min",
    hoursAgo: "h",
    daysAgo: "d",
  },
  landing: {
    hero: {
      tagline: "Your AI partner for daily planning",
      description: "Plan your day with AI, track progress on your goals, and create a journal that lets you review achievements and activities for each day.",
      cta: "Get started",
      login: "Login",
      register: "Register",
      registerNote: "Access will be granted after administrator approval.",
      betaBadge: "Beta - free to use",
      openApp: "Open app",
    },
    features: {
      title: "Features",
      subtitle: "Everything you need for a productive day",
      goals: {
        title: "Hierarchical goals",
        description: "Yearly, monthly, and weekly goals with automatic progress tracking. Connect daily tasks with broader goals.",
        badge: "Unique",
      },
      ai: {
        title: "AI daily planning",
        description: "Intelligent assistant that helps you plan your day based on your goals, tasks, and calendar events.",
      },
      todoist: {
        title: "Todoist integration",
        description: "Sync tasks directly with Todoist. Import, edit, and add tasks without leaving the app.",
      },
      calendar: {
        title: "Google Calendar",
        description: "View calendar events and include them in your daily plan. Never miss a meeting.",
      },
      archive: {
        title: "Archive and timeline",
        description: "Browse past entries on a timeline. Export your journal in markdown format.",
      },
      email: {
        title: "Morning and evening email",
        description: "Receive a morning task overview and evening reminder to finish your day directly to your email.",
      },
      absences: {
        title: "Absence management",
        description: "Mark vacation or absence days. Automatically adjusts email notifications.",
      },
    },
    howItWorks: {
      title: "How it works",
      subtitle: "6 steps to a productive day",
      steps: [
        { title: "Priorities", description: "Tell the AI what you'd like to do today and what's important." },
        { title: "Review", description: "AI analyzes your tasks, goals, and calendar and prepares suggestions." },
        { title: "Task selection", description: "Choose tasks for today from your Todoist list and AI suggestions." },
        { title: "Daily plan", description: "AI creates a daily plan and syncs changes with Todoist." },
        { title: "Evening review", description: "At the end of the day, review what was done and write a reflection." },
        { title: "Journal", description: "AI composes a journal entry based on the entire conversation." },
      ],
    },
    footer: {
      copyright: "Journail. All rights reserved.",
    },
    cover: {
      edgeLeft: "Journail · MMXXVI",
      edgeRight: "Issue No. 01 · Cover",
      nav: {
        product: "Product",
        method: "Method",
        pricing: "Pricing",
        manifesto: "Manifesto",
        changelog: "Changelog",
        faq: "FAQ",
        blog: "Blog",
        signIn: "Sign in",
        startTrial: "Start free trial",
        guides: "Guides",
        productMenu: [
          { label: "Daily rhythm", desc: "Your 4-step cycle", href: "/product/daily-rhythm" },
          { label: "AI Assistant", desc: "How the AI works", href: "/product/ai-assistant" },
          { label: "Goals", desc: "Yearly to weekly, aligned", href: "/product/goals" },
          { label: "Journal", desc: "Writes itself from your day", href: "/product/journal" },
          { label: "Integrations", desc: "Google Calendar and Todoist", href: "/product/integrations" },
        ],
        guidesMenu: [
          { label: "Tutorials", desc: "Quick tips and shortcuts", href: "/guides/tutorials" },
          { label: "Our Trail", desc: "What's changed, release by release", href: "/guides/our-trail" },
          { label: "Stories", desc: "Articles on reflection and focus", href: "/blog" },
          { label: "FAQ", desc: "Technical and product questions", href: "/faq" },
        ],
      },
      hero: {
        kicker: "// THE ALIGNMENT LOOP",
        headline: {
          line1A: "A",
          line1Em: "calm planner",
          line1B: " by morning.",
          line2A: "A",
          line2Em: "mindful journal",
          line2B: " by night.",
        },
        purposeLine: "AI daily planner & journal",
        description:
          "Journail is a smart daily planner and journal in one. Stop firefighting urgent tasks. Bring your long-term goals and daily focus into **perfect alignment**. Spend just 5 minutes a day using guided AI assistance to write down your morning plan and capture your thoughts in an **effortless evening journal**.",
        descriptionShort:
          "A smart daily planner and journal in one. Plan your morning, capture your evening — 5 minutes a day, guided by AI.",
        ctaPrimary: "Start Free Trial",
        ctaSecondary: "See how it works",
        trust: [
          "7-day trial.",
          "Then €7/mo.",
          "No credit card required.",
          "Your data never trains AI models.",
        ],
        calendarNote: "Google Calendar is optional. Journail only reads your events and adds the ones you schedule. Disconnect anytime.",
        fig: "FIG. 01",
        figLabel: "Daily Rhythm",
        figSub: "A day that pulls toward what matters.",
        nodes: [
          { code: "6:30", label: "Daily Plan" },
          { code: "9:00", label: "Daily Flow" },
          { code: "18:30", label: "Daily Journal" },
          { code: "22:00", label: "Tomorrow's Preview", optional: true, optionalLabel: "optional" },
        ],
        goalsLabel: "THE GOAL GRAVITY",
        goalsSub: "YEARS · MONTHS · WEEKS",
      },
      chapters: [
        {
          no: "01 / THE DAILY BRIEF",
          title: "Plan the day in five minutes.",
          body: "AI reads your goals, calendar, and tasks, then suggests two tasks — not twenty.",
        },
        {
          no: "02 / DEEP WORK",
          title: "Your focus, evolving.",
          body: "A quiet timeline that stays with you. Check off tasks, add thoughts, or ask AI as you go. No push pings.",
        },
        {
          no: "03 / REVIEW",
          title: "Understand it in two.",
          body: "Reflect on what moved and what stalled. Through a brief conversation, AI captures your thoughts into a journal entry, kept in your unique voice.",
        },
        {
          no: "04 / PLAN AHEAD",
          title: "For a calm start.",
          body: "Optional: Complete your Daily Brief tonight. Get your intentions out of your head and into Journail, so you can wake up with total clarity.",
        },
      ],
      showcase: {
        kicker: "// HOW IT WORKS",
        h2: "One cycle. Less than 5 minutes a day.",
        learnMore: "Explore the Daily Rhythm",
        phases: [
          {
            no: "01 / Daily Plan",
            title: "From a blank canvas to total alignment.",
            body: "When you start your day, Journail checks your long-term goals and pending tasks to craft a balanced blueprint for today. It acts as a protective shield, ensuring that minor tasks never overshadow your big milestones.",
          },
          {
            no: "02 / Daily Flow",
            title: "Your quiet hub for real-time focus.",
            body: "Once your plan is set, Journail becomes your central harbor for the day. Close completed tasks, add new ones on the fly, or capture quick notes before they slip away. Need to review a past pattern? Use the side AI assistant to analyze your history in natural language — all within a single tab open in your browser.",
          },
          {
            no: "03 / Daily Journal",
            title: "Turn daily effort into a lasting legacy.",
            body: "At the end of the day, look back at your progress. If there's something you are truly proud of, write it down. Journail's AI automatically blends your completed tasks, notes, and highlights into a beautifully formatted journal entry. Your day closes, and your growth is saved forever.",
          },
          {
            no: "04 / Tomorrow's Preview",
            title: "Offload your mind before sleep.",
            body: "An optional evening ritual for those who want to sleep with a completely free head. Sketch out tomorrow's key focus in a few words, close the laptop, and rest deeply knowing everything is captured.",
          },
        ],
        screens: {
          plan: {
            date: "MON · JUNE 8",
            title: "Today's plan",
            tasks: [
              { title: "Draft the monthly strategy", time: "09:00" },
              { title: "Deep focus: launch prep", time: "10:30" },
              { title: "Sync with the team", time: "14:00" },
              { title: "Run along the river", time: "18:00" },
            ],
          },
          flow: {
            date: "MON · 13:42",
            title: "Today's focus",
            tasks: [
              { title: "Draft the monthly strategy", time: "09:00" },
              { title: "Deep focus: launch prep", time: "10:30" },
              { title: "Sync with the team", time: "14:00" },
            ],
            focus: "FOCUS · 52 MIN",
          },
          journal: {
            date: "MON · END OF DAY",
            title: "Today's journal",
            highlight: "The strategy finally clicked into place.",
            body: "Three deep hours of work, no interruptions. The team sync opened a direction I hadn't seen before.",
            reflection: "The best work happens when the morning stays untouched.",
          },
          tomorrow: {
            date: "TOMORROW · TUE",
            title: "Tomorrow's preview",
            intentions: [
              "Finish the deck before lunch.",
              "One screen-free hour in the evening.",
            ],
            note: "Rest well. →",
          },
        },
      },
      journalSection: {
        kicker: "// THE JOURNAL",
        h2a: "Your history is",
        h2em: "your finest teacher.",
        description: "A mosaic of your moments. Look back to step forward. While regular planners delete your history once a task is checked off, Journail preserves your journey. Every completed milestone, every quick note, and every moment of pride is woven into a beautifully organized archive.",
        growth: "Your journal is not just a storage room for the past — it is your finest teacher. By looking back at your trail, you discover your true energy patterns, understand what holds you back, and consciously improve your focus week after week. Turn your daily effort into compounding wisdom.",
        annotationTag: "A PROUD MOMENT",
        annotationLabel: "Something I'm proud of.",
        learnMore: "More about journal",
        cardDate: "Wednesday · May 6, 2026",
        cardEntry: "First full block of deep work in two weeks.",
        cardBody: "Quiet day. Deep work on the board memo and a long walk after lunch. Came back to a clean afternoon.",
      },
      assistant: {
        kicker: "AI ASSISTANT",
        h2a: "A companion that actually",
        h2em: "knows you.",
        learnMore: "Explore the AI Assistant",
        description: "Journail features an integrated side assistant that doesn't just process text — it understands your life. Armed with the full context of your high-level goals and your last 14 days of entries, it acts as a personal strategist, memory booster, and rapid editor in one calm interface.",
        ctaIntro: "Try the assistant live. Click any command below to see how it works:",
        commands: [
          "/task Review landing page copy tomorrow at 10 AM",
          "What was holding back my focus this week?",
          "Summarize my key achievements from June",
        ],
        demoYou: "You",
        demoThinking: "Journail is thinking…",
        demoIdle: "Click a command on the left to see the assistant in action.",
        demoReplayHint: "Click another command for a new example.",
        demoScrollHint: "Scroll to continue ↓",
        demoUnderstood: "Got it. Here's what I understood:",
        demoConfirmLabel: "Please confirm by clicking:",
        demoChipVerb: "Add task",
        demoChipTip: "Tomorrow at 10:00 · I'll link it to your “Launch” goal.",
        demoTaskTitle: "Review landing page copy",
        demoTaskWhen: "Tomorrow · 10:00 AM",
        demoResponses: [
          "Done — the task is added to tomorrow's plan and linked to your “Launch” goal.",
          "Looking at the last 7 days: your deepest focus came in the mornings, before 11:00. Three afternoons were broken up by unplanned admin tasks — that's where momentum stalled. Two of your top goals didn't get a dedicated block. Next week, protect one morning slot for your biggest goal.",
          "June, in short: you closed 18 of 22 planned tasks, kept a 12-day journaling streak, and made real progress on two of your three quarterly goals. Your proudest moment: finishing the project you'd postponed twice. A focused, steady month.",
        ],
        demoLabel: "DEMO",
        scrollCue: "SCROLL TO TRY IT",
        closerA: "Less a chatbot. More a ",
        closerEm: "memory that thinks with you.",
        steps: [
          {
            node: "Create",
            meta: "01 / Create a task",
            ctx: "Reads your goals · creates the task",
            slash: "/task ",
            cmd: "Review landing page copy tomorrow at 10 AM",
            tag: "Task created",
            ai: "Added for tomorrow at 10:00 and linked to your “Launch” goal. Want me to block 45 minutes around it?",
          },
          {
            node: "Reflect",
            meta: "02 / Reflect on your week",
            ctx: "Reads your last 14 days · finds patterns",
            slash: "",
            cmd: "What was holding back my focus this week?",
            tag: "Pattern found",
            ai: "Three days lost to unplanned meetings. Your deep-work blocks only held on Tuesday and Thursday — both mornings.",
          },
          {
            node: "Recap",
            meta: "03 / Recap the month",
            ctx: "Reads tasks, goals & journal · summarizes",
            slash: "",
            cmd: "Summarize my key achievements from June",
            tag: "June recap",
            ai: "Shipped the landing rewrite, closed 2 monthly goals, and kept 11 journal entries. Strongest week: Jun 8–12.",
          },
        ],
      },
      languageSection: {
        kicker: "// LANGUAGE FREEDOM",
        h2a: "An interface in English. A heart that speaks",
        h2em: "your language.",
        description:
          "While Journail's core interface remains in clean, professional English, your thoughts don't have to be. You can write your tasks, type your raw notes, and maintain your journal entries in whatever language you feel most comfortable with.",
        settingsNote:
          "In your settings, you can define exactly how you want to converse with your AI companion. Prefer to chat in Slovenian, German, or Spanish? The AI will seamlessly read your history, respond to your prompts, and generate your evening journal reflections in your language of choice. True alignment knows no borders.",
        mockBreadcrumb: "SETTINGS",
        mockLabel: "Content language",
        mockHint: "100+ languages",
      },
      aiAssistantPage: {
        commands: {
          kicker: "// NATURAL LANGUAGE",
          h2a: "From thought to execution",
          h2em: "in seconds.",
          description:
            "Two ways, one result. Type a quick command, or just write in plain, ordinary language — the assistant reads your intent, resolves it into a task (due date, project, goal) and shows you a confirmation chip. Nothing happens until you click.",
          naturalLabel: "Write naturally",
          naturalInput:
            "Add task Prepare proposal for ACME with due date beginning next week in project Sales and goal Revenue plan for Q3.",
          slashLabel: "… or a quick command",
          slashInput: "/task Prepare proposal for ACME · next week | project: Sales | goal: Revenue Q3",
          understood: "The assistant understood — confirm:",
          chipVerb: "Add task",
          chipDetail: "Prepare proposal for ACME · early next week · project: Sales · goal: Revenue Q3",
          confirmHint: "Nothing is created until you click the chip to confirm.",
          actionsLead: "Not just creating —",
          actions: ["Add", "Edit", "Complete", "Delete", "Move"],
          actionsTail: "tasks, goals, and events.",
        },
        memory: {
          kicker: "// ALWAYS IN CONTEXT",
          h2a: "Your goals and recent days,",
          h2em: "always in context.",
          description:
            "Generic AI models forget who you are the moment you close the tab. Journail's assistant always carries the full picture — every active goal you're chasing and the reflections of your recent days. Ask, and it connects the dots without you re-explaining your life.",
          pills: ["All active goals", "Recent reflections", "Open tasks"],
        },
        strategy: {
          kicker: "// STRATEGIC CONVERSATION",
          h2a: "See your patterns.",
          h2em: "Plan your growth.",
          description:
            "Your entries aren't passive text — they're signal. Ask the assistant what's working and what's pulling you off course, and it answers from your goals and your recent reflections.",
          dialogue: [
            {
              user: "Across my active goals, where am I losing momentum?",
              ai: "Product work is on track. But your reflections this past week mention unplanned support work crowding out deep focus on three days. Want me to protect a morning block next week for your top goal?",
            },
            {
              user: "What's been draining my focus lately?",
              ai: "Your recent evening reflections cluster low-focus days right after back-to-back morning meetings. Your proudest days were the ones where you guarded the first hours for deep work.",
            },
          ],
        },
      },
      dailyRhythmPage: {
        anatomy: {
          kicker: "// THE WORKSPACE",
          h2a: "One screen,",
          h2em: "three quiet columns.",
          description:
            "Journail isn't a dashboard of widgets fighting for attention. It's one calm workspace: your rhythm on the left, your conversation in the center, your tasks and goals on the right.",
          columns: [
            {
              tag: "LEFT · THE LOOP",
              title: "Where you are in the day",
              body: "A four-node loop shows the current phase, with the active step softly pulsing. Below it, your upcoming events; below that, recent highlights — the moments you were proud of in your latest journals.",
            },
            {
              tag: "CENTER · THE THREAD",
              title: "The conversation",
              body: "The middle column is a thread, not a form. The whole day plays out here — the morning brief, picking tasks, the plan, focus, and the evening close — with one primary action at a time and a composer at the bottom.",
            },
            {
              tag: "RIGHT · TASKS & GOALS",
              title: "Your context, on hand",
              body: "Active tasks first, with priority and label — whether they live in Journail, Todoist, or Google Calendar. Beneath them, your long-term goals, reading like prose rather than chips.",
            },
          ],
        },
        process: {
          kicker: "// A FULL DAY",
          h2a: "How a day flows,",
          h2em: "from morning to entry.",
          description:
            "It all happens in the center thread. In the morning you draft the plan together in a few minutes; through the day you work and chat; in the evening the day melts into a journal entry. Here's the whole process, step by step.",
          acts: [
            {
              no: "01",
              phase: "MORNING · PLAN",
              title: "Draft the plan in a short conversation.",
              body: "Planning isn't filling in a form — it's a guided conversation where the assistant brings the context and you make the calls.",
              steps: [
                "The Daily Brief greets you with two questions: what really has to move today, and what's quietly weighing on you.",
                "First, you say what you've already done or what your main priority for today is.",
                "Then you select: the assistant offers tasks from your planner, plus suggestions from your goals and calendar — you pick what you'll do, add your own, and push the rest to tomorrow.",
                "From your picks it drafts a plan. Write a note to it, edit the plan, or confirm. Confirming sets today's plan and the day moves into Focus.",
              ],
            },
            {
              no: "02",
              phase: "DAYTIME · FOCUS",
              title: "Through the day, you just work.",
              body: "Once the plan is set, the workspace becomes your harbor for the whole day.",
              steps: [
                "Today's plan sits in front of you — check tasks off and watch progress tick up.",
                "Chat with the AI Assistant, add new tasks, or jot a quick note before it slips away.",
                "Every completion and note quietly threads into a record of the day. When you're ready, you press \"Close the day\".",
              ],
            },
            {
              no: "03",
              phase: "EVENING · ENTRY",
              title: "The day melts into a journal.",
              body: "At day's end you kick off the journal entry — a guided close in three steps.",
              steps: [
                "First, a task review: mark what's done, and on the open ones note what's still needed.",
                "The assistant drafts the entry — completed tasks, goal connections, and a summary. You confirm it or edit.",
                "Optionally add what was truly important today. The entry is saved to your timeline — \"Today's story is written.\" Tomorrow, you open a new page.",
              ],
            },
          ],
        },
      },
      toolkit: {
        kicker: "// TRUST & FLEXIBILITY",
        h2a: "Completely standalone. Built to",
        h2em: "respect your world.",
        learnMore: "Explore integrations",
        cards: [
          {
            title: "Your tasks, zero friction.",
            body: "Journail comes with its own robust, minimalist task system designed for pure execution. It works beautifully right out of the box as your independent workspace.",
          },
          {
            title: "Connect your ecosystem.",
            body: "Journail is an AI journal for daily planning. Connect Google Calendar in one click: Journail reads your calendar events to build your daily plan, and adds or updates the events you schedule from the planner. Todoist and email capture work the same way — every integration is optional and you can disconnect it anytime.",
          },
          {
            title: "Your thoughts stay yours. Period.",
            body: "A journal is the most intimate space you own. Our admin dashboard has zero-knowledge access to your entries. Your 14-day AI context is processed securely and is never used to train public LLM models.",
          },
        ],
      },
      productPages: {
        ctaLine: "Start your 7-day trial.",
        dailyRhythm: {
          kicker: "// DAILY RHYTHM",
          h2: "Strategic daily planning, in four calm steps.",
          lead: "Plan toward your goals in the morning, protect focus for deep work through the day, and close with a structured daily reflection at night. Five minutes a day keeps your work in daily alignment with what matters — the rhythm does the rest.",
        },
        aiAssistant: {
          kicker: "// THE AI ASSISTANT",
          h2: "An AI productivity coach, fluent in your goals.",
          lead: "Stop hopping between planners, to-do apps, and generic AI tabs. Journail's assistant lives inside your workspace as an AI task manager and coach — it knows every goal you're chasing and your recent entries, plans and edits in plain language, and turns each evening into AI-powered reflection.",
        },
        goals: {
          kicker: "// GOALS",
          h2: "A goal planning app that keeps you in daily alignment.",
          lead: "Set yearly, monthly, and weekly goals, then link your daily tasks to them. Automated goal tracking and a daily alignment tool keep your day pulling toward what matters — not just toward what's urgent.",
        },
        journal: {
          kicker: "// JOURNAL",
          h2: "A productivity journal that writes itself.",
          lead: "Journail is an AI journal and reflective journal app in one: a digital planner with AI by morning that turns your real work into an entry by night. Your archive becomes a searchable memory of decisions, patterns, and progress.",
        },
        integrations: {
          kicker: "// INTEGRATIONS",
          h2: "Your all-in-one organizer. Optional by design.",
          lead: "Journail is an all-in-one organizer for work and life that stands on its own. When you want, connect Google Calendar or Todoist as your AI task manager, and feed your journal straight by email — no setup marathon, no lock-in.",
        },
      },
      method: {
        kicker: "// THE METHOD",
        headline: "One loop. Four phases.\nA better trajectory every day.",
        p1: "Most productivity apps give you tools. Journail gives you a rhythm.",
        p2: "To start, AI reads your calendar, your goals, and your open tasks — and helps you decide what matters today. Not twenty tasks. Two or three.",
        p3: "When you're done, the same AI helps you look back. The conversation becomes your journal entry, kept in your voice and preserved in your timeline.",
        close: "A year from now, you'll have something worth reading.",
      },
      problem: {
        kicker: "// THE REALITY",
        titleA: "The cost of",
        titleEm: "fragmented days.",
        description:
          "Your tasks live in one tool. Your calendar in another. And your long-term goals? They are quietly waiting for a free moment that never comes. You end your days exhausted, checking off endless minor items, while wondering if you actually made any progress on what truly matters.",
        solution:
          "Journail changes how your day is built. By placing your high-level vision directly into your daily workspace, it ensures that your big goals are never pushed aside by urgent noise.",
      },
      day: {
        kicker: "// A DAY IN JOURNAIL",
        headline: "Four moments. One quiet flow.",
        tabs: [
          { time: "06 : 30", label: "Morning Brief" },
          { time: "09 : 00", label: "Focus" },
          { time: "18 : 30", label: "Evening Debrief" },
          { time: "20 : 00", label: "Today's Story" },
        ],
        figLabel: "FIG. 02 · MORNING BRIEF",
        sidebar: {
          today: "Today",
          archive: "Archive",
          items: ["Morning Brief", "Focus", "Evening Debrief", "Story"],
          archiveItems: ["Apr 20 · Monday", "Apr 19 · Sunday", "Apr 18 · Saturday"],
        },
        mockDate: "Monday · April 20 · 06 : 34",
        mockHeading: "Good morning. Three things on your mind today.",
        mockAi1:
          "You have the alignment spec review at 14:00 and a stuck thread with Sofia from Friday. Your Q2 goal is still \u201Cship v1 to 50 users.\u201D What feels most important today?",
        mockYou: "The alignment spec. I promised Sofia a draft by noon.",
        mockAi2:
          "Got it. Blocked out 09:30\u201312:00 for the draft, then a 30-min buffer before the review. I\u2019ll quiet everything else until noon.",
        mockWho: { ai: "Journail", you: "You" },
        rightTitle: "Today's plan",
        rightGoal: "Goal in view",
        tasks: [
          { label: "Draft alignment spec for Sofia", meta: "09 : 30 \u2014 12 : 00 · Deep work" },
          { label: "Alignment spec review", meta: "14 : 00 \u2014 15 : 00 · Calendar" },
          { label: "Reply to Marcus on pricing thread", meta: "15 : 30 · 10 min" },
        ],
        goalLabel: "Ship v1 to 50 users",
        goalMeta: "Q2 goal · Week 3 of 12",
        more: "See Focus, Evening Debrief and Today's Story \u2192",
      },
      compare: {
        kicker: "// HOW IT COMPARES",
        headlineA: "The only one that closes",
        headlineEm: "the loop.",
        lead: "Productivity apps plan your day. Journaling apps record it. Journail is the only one that does both \u2014 with AI that remembers context.",
        featureHead: "Feature",
        journailHead: "Journail",
        competitors: ["Sunsama", "Motion", "Rosebud", "Day One"],
        rows: [
          { feature: "AI morning planning", values: [true, false, true, false, false] },
          { feature: "AI evening reflection", values: [true, false, false, true, false] },
          { feature: "Auto-generated journal", values: [true, false, false, false, false] },
          { feature: "Hierarchical goals", values: [true, false, false, false, false] },
        ],
        note: "// Based on publicly available features as of April 2026.",
        dragHint: "Drag to compare →",
        seeMore: "Read the full comparisons",
        allComparisons: "All comparisons",
      },
      integrations: {
        kicker: "// INTEGRATIONS",
        headline: "Works with what you already use.",
        items: [
          {
            name: "Todoist",
            desc: "Optional — Journail has its own built-in task manager, so you need nothing else. Prefer Todoist? Connect it for two-way sync: your morning plan writes back as tasks with due dates, and completed tasks sync from either side.",
          },
          {
            name: "Google Calendar",
            desc: "Two-way sync, not read-only. Journail reads your events to plan your day, and creates or updates the events you schedule from the planner. Works across multiple calendars and accounts.",
          },
          {
            name: "Email",
            desc: "Your morning brief arrives in your inbox. Your evening debrief waits for you there too. No push notifications that break focus.",
          },
        ],
        foot: "// More integrations coming. Apple Reminders · Microsoft To Do · Notion.",
      },
      faq: {
        kicker: "// FAQ",
        headlineA: "Questions,",
        headlineEm: "before you start.",
        items: [
          {
            q: "Does Journail train AI models on my journal entries?",
            a: "No. Your words are encrypted in transit and at rest, used only to generate your own responses, and never used for training models — ours or anyone else's.",
          },
          {
            q: "What happens when my 7-day trial ends?",
            a: "Nothing automatic. We don't ask for a credit card upfront, so we can't surprise you. After 7 days you continue on the Free tier until you choose to upgrade.",
          },
          {
            q: "How is this different from Notion or a plain journal?",
            a: "Notion is a blank canvas. A journal is a blank page. Journail is a structured rhythm — AI that reads your context (calendar, goals, tasks) and helps you plan and reflect.",
          },
          {
            q: "Can I export everything I write?",
            a: "Any time. Markdown for all entries, JSON for structured data. Your words are yours. If you leave, you take everything with you.",
          },
        ],
      },
      pricing: {
        kicker: "// PRICING",
        headlineA: "Priced in whole numbers.",
        headlineB: "No tricks.",
        toggleMonthly: "Monthly",
        toggleYearly: "Yearly",
        toggleSave: "Save ~22%",
        perMonth: "/month",
        forever: "forever",
        noCard: "No credit card · Cancel anytime",
        billedYearly: "Billed yearly",
        billedMonthly: "Billed monthly",
        badgePopular: "Most Popular",
        vatIncluded: "VAT included",
        plans: [
          {
            eyebrow: "PRO",
            title: "Close the loop daily.",
            priceYearly: "€7",
            priceMonthly: "€9",
            subYearly: "€84 billed yearly — save €24",
            subMonthly: "Billed monthly",
            cta: "Start 7-day free trial",
            features: [
              "Standard AI access (Generous daily limits, perfect for your daily routine)",
              "Up to 2 Google Calendars",
              "Full journal archive",
              "Hierarchical goals (year → month → week)",
              "Todoist two-way sync",
              "Morning + evening email briefs",
              "Markdown + JSON export",
              "Absence tracking",
              "Light + dark mode",
            ],
          },
          {
            eyebrow: "PREMIUM",
            title: "For power users.",
            priceYearly: "€12",
            priceMonthly: "€15",
            subYearly: "€144 billed yearly — save €36",
            subMonthly: "Billed monthly",
            cta: "Start 7-day free trial",
            features: [
              "Everything in Pro, plus:",
              "Supercharged AI access (Expanded limits built for deep conversations and high-frequency use)",
              "Unlimited Google Calendars",
              "Advanced AI insights (patterns, sentiment) [soon]",
              "Priority support",
              "Early access to new features",
            ],
          },
        ],
        promise: {
          kicker: "// OUR PRICING PROMISE",
          text: "Your trial won't auto-convert without asking. We'll never charge you for something you forgot to cancel.",
          coda: "And the longer you use Journail, the more it gives back — your archive is yours, exportable any time, growing more valuable with every day you write.",
          sign: "— THE JOURNAIL TEAM",
        },
        bridge: "One price for a daily planner and journal in one. The archive grows for free; the Journail to read it back to you is what you pay for.",
      },
      archive: {
        kicker: "// THE ARCHIVE",
        headlineA: "A year of you,",
        headlineEm: "in your own words",
        headlineB: ".",
        leadA: "Most apps file your past. Journail uses it.",
        leadB: "Every Morning Brief, every plan, every Evening Debrief becomes part of an archive that AI helps you read — not as nostalgia, but as feedback.",
        leadC: "Six months in, you'll see things no calendar can show you. Patterns in how you start your weeks. Goals you mentioned but never moved. Days you'd forgotten that turned out to matter.",
        soft: "Soon, your archive will start to talk back. Patterns AI sees that you don't.",
        link: "See the roadmap →",
        botDate: "Wed · Apr 22 · 2026",
        botPreview: "A slow start, then a quiet stretch of focus on the proposal — the kind of day that doesn't feel like much until you read it back.",
        hint: "AI saw a pattern this week.",
        midDate: "Thu · Apr 23 · 2026",
        midPreview: "Held the morning for deep work and it paid off — the spec finally clicked. Walked the long way home to think about what comes next.",
        midMeta: "2 tasks completed · linked to 1 goal",
        topDate: "Friday · April 24 · 2026",
        topClosed: "Closed",
        topQuote: "Closed the loop on the partner brief. Honest about what's still soft — we ship Tuesday either way.",
        topPreview: "Today held together. Morning went to the brief, which is now solid enough to send. The afternoon call with the team turned a vague worry into a decision — we'll narrow scope rather than slip the date. Felt the pull of the bigger goal again, after a wobbly week.",
        topMeta: "3 tasks completed · 1 calendar event · linked to 2 goals",
        entries: [
          {
            date: "THU · MAY 7",
            quote: "Mayo Clinic moved from pilot to paid — our largest contract yet.",
            preview: "Their cardiology chief said the staff stopped reaching for the old monitors entirely. The team's reaction made it clear how much momentum this gives us.",
            meta: "8 min · linked to 2 goals",
          },
          {
            date: "WED · MAY 6",
            quote: "First full thinking block in two weeks. The memo finally feels honest.",
            preview: "Wrote the board memo from scratch — earlier drafts felt too polished. This one tells the truth: we're shipping slower than I hoped, and the customers we have love us more than I expected.",
            meta: "11 min · linked to 3 goals",
          },
          {
            date: "TUE · MAY 5",
            quote: "Said no to a fast-track pilot we couldn't service well. Integrity compounds.",
            preview: "Cleveland Clinic wanted six units in 30 days. We can't service that without straining the support team. Pushed back, suggested a phased start.",
            meta: "9 min · linked to 1 goal",
          },
          {
            date: "MON · MAY 4",
            quote: "Sasha caught a risk I'd been underweighting. Trust earned by listening.",
            preview: "She said the FDA timeline I'd been pushing wasn't realistic with the current evidence package. She was right. Adjusted plan, took ownership of the clinical narrative.",
            meta: "14 min · linked to 2 goals",
          },
          {
            date: "FRI · MAY 1",
            quote: "Pipeline coverage moved from 2.1× to 3.4× once we deduped duplicates.",
            preview: "A 90-minute audit shifted my mental model of the business more than the last three forecast meetings combined. Reporting was the bottleneck, not the funnel.",
            meta: "7 min · linked to 2 goals",
          },
          {
            date: "THU · APR 30",
            quote: "The hardware in hand reads different than on screen.",
            preview: "The first manufacturing batch arrived. The bevel on the clip is finally right. Battery seating still feels too tight — filed a fix request for the next batch.",
            meta: "5 min · linked to 1 goal",
          },
        ],
      },
      founder: {
        kicker: "// A NOTE FROM THE FOUNDER",
        paragraphs: [
          "I built Journail because I got tired of managing my day across five apps and still feeling lost at the end of it.",
          "Notion held my goals. Todoist held my tasks. My calendar held my meetings. And somewhere between all of that, I stopped journaling — even though I missed it — because writing \"what happened today?\" from scratch every night felt like homework.",
          "Journail is what I wished existed. One place where AI helps me plan in the morning, reflect in the evening, and quietly turns each day into something I'll actually want to read six months from now.",
          "I hope it does the same for you.",
        ],
        signName: "— Tomaž P.",
        signRole: "Founder, Journail",
      },
      socialProof: {
        kicker: "// THE COMMUNITY & THE VISION",
        h2a: "Built for",
        h2em: "thinkers and doers.",
        quotes: [
          {
            text: "Journail is the first tool that didn't demand I change my entire workflow. It just quietly helps me align what I do today with where I want to be next year.",
            author: "Luka · Software Engineer",
          },
          {
            text: "Finally, a space that values privacy and calm. The AI assistant feels like a personal strategist who reminds me of my progress instead of just listing missing items.",
            author: "Sarah · VP Product",
          },
        ],
        founderTitle: "Why I built Journail",
        founderLetter: [
          "Like many of you, I spent years trapped in a loop of endless tasks. I would finish my days exhausted, having crossed off dozens of items, yet feeling like my long-term goals hadn't moved an inch. My thoughts were scattered across notes, apps, and mental noise.",
          "I built Journail because I needed a protective shield. I wanted a space that doesn't push you to do more, but helps you do what actually matters. A system where your daily action balances with your vision, and where your efforts are beautifully preserved into compounding wisdom over time.",
          "Thank you for being part of this rhythm.",
        ],
        founderSignature: "— Tomaž, Founder of Journail",
      },
      finalCta: {
        kicker: "// THE NEXT STEP",
        line1: "Don't change your planner.",
        em: "Change yourself.",
        description: "This is not about an application; it's about a 5-minute daily commitment to becoming a more aligned version of you. Your journey starts when you stop rushing. Start your 7-day free trial and experience the transformation.",
        cta: "Begin Your Alignment",
        micro: "7-day trial. Then €7/mo. No credit card required.",
      },
      footerNav: {
        product: {
          heading: "Product",
          links: [
            { label: "Daily rhythm", href: "/product/daily-rhythm" },
            { label: "AI Assistant", href: "/product/ai-assistant" },
            { label: "Goals", href: "/product/goals" },
            { label: "Journal", href: "/product/journal" },
            { label: "Integrations", href: "/product/integrations" },
          ],
        },
        company: {
          heading: "Company",
          links: [
            { label: "About", href: "/about" },
            { label: "Contact", href: "/contact" },
          ],
        },
        resources: {
          heading: "Guides",
          links: [
            { label: "Tutorials", href: "/guides/tutorials" },
            { label: "Our Trail", href: "/guides/our-trail" },
            { label: "Stories", href: "/blog" },
            { label: "Compare", href: "/compare" },
            { label: "FAQ", href: "/faq" },
          ],
        },
        legal: {
          heading: "Legal",
          links: [
            { label: "Privacy Policy", href: "/privacy" },
            { label: "Terms of Service", href: "/terms" },
            { label: "Cookie Policy", href: "/cookies" },
            { label: "AI Disclosure", href: "/ai" },
            { label: "GDPR Rights", href: "/gdpr" },
          ],
        },
        folio: "// THE ALIGNMENT LOOP · ISSUE NO. 01",
      },
    },
  },
  auth: {
    login: {
      title: "Login",
      subtitle: "Sign in to your AI journal",
      email: "Email",
      emailPlaceholder: "your@email.com",
      password: "Password",
      submit: "Sign in",
      noAccount: "Don't have an account?",
      register: "Register",
      error: "Invalid email or password",
      pendingError: "Your account is not yet approved. Please wait for administrator approval.",
      genericError: "Login error",
      googleSignIn: "Sign in with Google",
      forgotPassword: "Forgot password?",
    },
    register: {
      title: "Register",
      subtitle: "Create a new account for Journ",
      subtitleHighlight: "ai",
      subtitleEnd: "l",
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "your@email.com",
      password: "Password",
      passwordPlaceholder: "At least 8 characters",
      submit: "Register",
      hasAccount: "Already have an account?",
      login: "Login",
      error: "Registration error",
      existsError: "A user with this email already exists",
      pendingTitle: "Registration successful!",
      pendingMessage: "Your account is pending administrator approval. We'll notify you by email when your account is activated.",
      pendingBack: "Back to login",
    },
    forgot: {
      title: "Forgot password",
      subtitle: "Enter your email and we'll send you a link to reset your password.",
      email: "Email",
      emailPlaceholder: "your@email.com",
      submit: "Send link",
      sending: "Sending…",
      backToLogin: "Back to login",
      sentTitle: "Check your inbox",
      sentMessage: "If an account exists for that email, we've sent a password reset link. The link is valid for 1 hour.",
      genericError: "Something went wrong. Please try again.",
    },
    reset: {
      title: "New password",
      subtitle: "Enter a new password for your account.",
      password: "New password",
      passwordPlaceholder: "At least 8 characters",
      confirm: "Repeat password",
      confirmPlaceholder: "Repeat new password",
      submit: "Save password",
      saving: "Saving…",
      successTitle: "Password updated",
      successMessage: "Your password has been changed. You can now sign in with your new password.",
      goToLogin: "Go to login",
      mismatch: "Passwords don't match.",
      invalidToken: "This link is invalid or has expired. Please request a new one.",
      requestNew: "Request a new link",
      genericError: "Something went wrong. Please try again.",
    },
  },
  nav: {
    journal: "Today",
    archive: "Journal",
    goals: "Goals",
    tasks: "Tasks & calendar",
    calendar: "Calendar",
    faq: "FAQ",
    guides: "Guides",
    privacy: "Privacy",
    terms: "Terms",
    settings: "Settings",
    admin: "Admin",
    users: "Users",
    support: "Support",
    activity: "Activity",
    logout: "Sign out",
    openMenu: "Open menu",
  },
  admin: {
    title: "Administration",
    subtitle: "System overview and statistics",
    stats: {
      users: "Users",
      entries: "Entries",
      goals: "Goals",
      pending: "Pending",
    },
    links: {
      users: "Manage users",
      support: "Support messages",
      logs: "Activity log",
    },
    registrations: "Registrations (30 days)",
    entriesChart: "Entries (30 days)",
    date: "Date",
    count: "Count",
    users: {
      title: "Users",
      subtitle: "Manage users and roles",
      name: "Name",
      email: "Email",
      role: "Role",
      registered: "Registered",
      roleAdmin: "Admin",
      roleUser: "User",
      rolePending: "Pending",
      approve: "Approve",
      roleChanged: "Role changed.",
      roleChangeError: "Error changing role.",
      deleteUser: "Delete user",
      deleteConfirm: "Are you sure you want to delete this user? All their data will be permanently deleted.",
      deleteSuccess: "User deleted.",
      deleteError: "Error deleting user.",
      deleteSelf: "You cannot delete your own account.",
      language: "Language",
      lastActivity: "Last activity",
      expandProfile: "Expand profile",
    },
    logs: {
      title: "Activity log",
      subtitle: "Overview of all system actions",
      today: "Today",
      days7: "7 days",
      days30: "30 days",
      all: "All",
      time: "Time",
      type: "Type",
      message: "Message",
      user: "User",
      noLogs: "No records",
      types: {
        user_registered: "Registration",
        user_approved: "Approval",
        user_deleted: "User deleted",
        user_login: "Login",
        role_changed: "Role change",
        support_ticket: "Message",
        journal_created: "New entry",
        goal_created: "New goal",
      },
      filterByType: "Filter by type",
      allTypes: "All types",
    },
  },
  journal: {
    title: "Journal",
    greeting: "Good morning",
    todayPlan: "Today's plan",
    startPlanning: "Start planning",
    continuePlanning: "Continue planning",
    editPlan: "Edit plan",
    finishDay: "Finish day",
    noPlan: "You don't have a plan for today yet.",
    startNew: "Start a new day",
    planAhead: "Plan ahead",
    unfinalizedEntries: "Unfinished entries",
    unfinalizedWarning: "Unfinished entries from previous days",
    markAsDone: "Mark as done",
    viewEntry: "View entry",
    setupTitle: "Set up integrations before starting",
    setupDescription: "For the full experience, connect Todoist",
    setupDescriptionCalendar: " and Google Calendar",
    setupDescriptionEnd: " in settings.",
    settingsButton: "Settings",
    unfinalizedTitle: "You have an unfinished journal",
    unfinalizedNotFinished: "is not yet finished.",
    unfinalizedHint: "Finish the previous day before starting a new one.",
    finishPrevious: "Finish previous day",
    entryExists: "Today's entry already exists",
    finalized: "Finalized",
    finishJournal: "Finish journal",
    openInArchive: "Open in archive",
    planAheadDesc: "Prepare plans for upcoming days today.",
    tomorrow: "Tomorrow",
    dayAfter: "Day after tomorrow",
    calendarEvents: "calendar events",
    tasksForDay: "tasks for this day",
    entryExistsWarning: "Entry for this day already exists",
    editExistingPlan: "Edit existing plan",
    loadingData: "Loading data...",
    dataError: "Error loading data",
    conversationError: "Error starting conversation",
    todoistSkipped: "Todoist phase skipped.",
    tasksUpdated: "tasks updated",
    tasksUpdateFailed: "failed",
    tasksUpdateError: "Error updating tasks",
    entrySaved: "Journal entry saved!",
    entrySaveError: "Error saving entry",
    dayFinished: "Day finished! Email sent. Refreshing page...",
    dayFinishError: "Error finishing day",
    refreshError: "Error refreshing tasks",
    startButton: "Start today's journal",
    startButtonLoading: "Starting...",
    calendar: "Calendar",
    noEvents: "No events for today",
    allDay: "(all day)",
    tasks: "Tasks",
    addTask: "Add task",
    taskPlaceholder: "Task name...",
    timePlaceholder: "Time",
    project: "Project",
    noProject: "No project",
    priorityUrgent: "Urgent",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityNormal: "Normal",
    create: "Create",
    priorityTasksSingular: "priority task",
    priorityTasksPlural: "priority tasks",
    priorityTasksMany: "priority tasks",
    andMore: "...and",
    noTasks: "No tasks",
    overdue: "Overdue",
    forToday: "For today",
    completedToday: "Completed today",
    upcoming: "Upcoming",
    activeGoals: "Active goals",
    goalYearly: "Yearly",
    goalMonthly: "Monthly",
    goalWeekly: "Weekly",
    tasksRefreshed: "Tasks refreshed",
    tasksRefreshError: "Error refreshing tasks",
    taskCompleted: "Task marked as completed",
    taskReopened: "Task reopened",
    taskUpdateError: "Error updating task",
    taskDeleted: "Task deleted",
    taskDeleteError: "Error deleting task",
    taskContentRequired: "Enter task content",
    taskCreated: "Task created",
    taskCreateError: "Error creating task",
    deleteTask: "Delete task",
    phases: {
      priorities: "Priorities",
      taskSelection: "Review & select",
      myPlan: "My plan",
      completed: "Done",
      taskReview: "Task review",
      comment: "Comment",
      journal: "Journal",
      editPlan: "Edit plan",
      todoist: "Todoist",
    },
    continueToPhase: "Continue to ",
    jumpTo: "Jump to:",
    inputPlaceholder: "Share what's on your mind, or jump ahead …",
    planRefinePlaceholder: "Press ↵ to confirm the plan, click Edit to revise, or write a note for Journail …",
    conversationDone: "Conversation complete. Journal entry saved to archive.",
    finalizingDay: "Finalizing...",
    finalizeDay: "Finish day",
    openInArchiveBtn: "Open in archive",
    editText: "Edit text",
    cancelEdit: "Cancel",
    submitEdits: "Save",
    confirmAndSave: "Confirm and save",
    saving: "Saving...",
    confirmAndContinue: "Confirm and continue",
    confirming: "Confirming...",
    saveAndFinish: "Save and finish",
    saveEntry: "Save entry",
    editBtn: "Edit",
    todayEntry: "Today's entry",
    taskReviewTitle: "Daily task review",
    taskReviewSubtitle: "Mark completed tasks and add notes",
    refresh: "Refresh",
    noTasksForToday: "No tasks for today.",
    optionalNote: "Optional note...",
    notePlaceholder: "Note (e.g. what's still needed)...",
    continueToReview: "Continue to review",
    whichTasksToday: "Which tasks will you do today?",
    overdueLabel: "OVERDUE",
    todayLabel: "TODAY",
    noTasksToSelect: "No tasks to select.",
    taskContentPlaceholder: "Task content...",
    projectLabel: "Project",
    dueDateToday: "Today",
    dueDateTomorrow: "Tomorrow",
    dueDateWeekend: "Weekend",
    dueDateNextWeek: "Next week",
    addBtn: "Add",
    highlightsTitle: "What was important today?",
    highlightsSubtitle: "Note key moments you want highlighted on the timeline (optional)",
    highlightsPlaceholder: "E.g. Successfully finished project X, received important news...",
    continueToMyPlan: "Continue to My plan",
    phaseCompleted: "✓ Done",
    aiError: "Error communicating with AI. Please try again.",
    taskReviewError: "Error submitting task review",
    tasksCompleted: "tasks completed!",
    cancelDelete: "Cancel delete",
    deleteBtn: "Delete",
    rescheduleKeep: "Keep as is",
    rescheduleTomorrow: "Move to tomorrow",
    rescheduleWeekend: "Move to weekend",
    rescheduleNextWeek: "Move to start of next week",
    selectAll: "select all",
    deselectAll: "deselect all",
    suggestionsFromGoals: "SUGGESTIONS FROM GOALS",
    addNewTask: "ADD NEW TASK",
  },
  archive: {
    title: "Archive",
    subtitle: "Past journal entries",
    export: "Export",
    noEntries: "No entries yet",
    finalized: "Finalized",
    draft: "Draft",
    loadMore: "Load more",
    noEntriesMessage: "No journal entries yet.",
    startJournalHint: "Start with a daily review on the Journal page.",
    searchPlaceholder: "Search journal entries…",
    searchNoResults: "No entries match your search.",
    searchResultsCount: "results",
  },
  goals: {
    title: "Goals",
    subtitle: "Goal management",
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    addGoal: "Add goal",
    noGoals: "No goals",
    status: {
      active: "Active",
      completed: "Completed",
      partial: "Partially completed",
      failed: "Not achieved",
    },
    yearlyGoals: "Yearly goals",
    monthlyGoals: "Monthly goals",
    weeklyGoals: "Weekly goals",
    add: "Add",
    noGoalsEmpty: "No goals. Add your first one!",
    noActiveGoals: "No active goals.",
    showArchived: "Show completed goals",
    hideArchived: "Hide completed goals",
    archivedGoals: "completed goals",
  },
  settings: {
    title: "Settings",
    profile: {
      sectionTitle: "My profile",
      title: "Profile",
      description: "Your name, as shown across the app and in emails.",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailHint: "Your email is tied to your account and can't be changed here.",
      avatarHint: "Your profile picture is pulled from your Google account.",
      save: "Save",
      saved: "Profile saved.",
      errorSave: "Couldn't save.",
      loading: "Loading …",
    },
    integrations: "Integrations",
    email: "Email notifications",
    absences: "Absences",
    language: "Language",
    languageDescription: "Choose interface language",
    uiLanguage: "Interface language",
    uiLanguageDescription: "Controls translations, buttons, menus and emails.",
    contentLanguage: "Content language",
    contentLanguageDescription: "The language the assistant replies in and the one you write your journal, goals and tasks in. Can differ from the interface language.",
    contentLanguagePlaceholder: "Pick a language",
    taskProvider: {
      title: "Task Management",
      description: "Choose your task management system",
      internal: "Internal",
      internalDescription: "Built-in task manager with recurring task support",
      todoistLabel: "Todoist",
      todoistDescription: "Sync tasks with your Todoist account",
      googleTasks: "Google Tasks",
      googleTasksDescription: "Sync tasks with Google Tasks",
      switchWarning: "Switching won't delete data in the previous system.",
      saved: "Task manager changed",
      saveError: "Error changing task manager",
      projects: "Projects",
      addProject: "Add project",
      projectName: "Project name",
      noProjects: "No projects. Add your first one.",
      projectAdded: "Project added",
      projectDeleted: "Project deleted",
      notImplemented: "Google Tasks integration is not yet available.",
    },
    todoist: {
      description: "Connect Todoist to import and manage tasks",
      connected: "Connected",
      disconnected: "Disconnected",
      connectedMessage: "Todoist is connected. Tasks are automatically imported into your journal.",
      disconnect: "Disconnect",
      apiKey: "API key",
      apiKeyPlaceholder: "Enter Todoist API key",
      apiKeyHelp: "Find it in",
      connect: "Connect",
      connectSuccess: "Todoist connected successfully!",
      connectError: "Error connecting",
      disconnectSuccess: "Todoist disconnected",
      disconnectError: "Error disconnecting",
    },
    calendar: {
      description: "Connect Google Calendar accounts to display events",
      disconnected: "Disconnected",
      addAccount: "Add Google account",
      connectCalendar: "Connect Google Calendar",
      loadingAccounts: "Loading accounts...",
      selectCalendars: "Select calendars:",
      saveSelection: "Save selection",
      noCalendarSelected: "No calendar selected - all will be shown.",
      calendarsCount: "calendars",
      connectError: "Error connecting Google Calendar",
      accountRemoved: "Google account removed",
      removeError: "Error removing account",
      calendarsSaved: "Selected calendars saved",
      saveError: "Error saving",
      verificationTitle: "Calendar connection is under Google review",
      verificationBody: "Journail is going through Google's approval for calendar access. Until it's done, when connecting you'll see Google's \"Google hasn't verified this app\" screen. This is expected and safe — it's our app. To continue, click \"Advanced\" and then \"Go to Journail\". Your data stays private; we only use it to show you your day.",
    },
    emailNotifications: {
      title: "Email notifications",
      description: "Receive reminders for morning or evening journal",
      emailAddress: "Email address",
      morningEmail: "Morning email",
      morningDescription: "AI-generated daily plan based on your goals, calendar, and tasks.",
      sendAt: "Sent at",
      eveningReminder: "Evening reminder",
      eveningDescription: "Reminder to finish your day with a link. If no entry exists, sends today's tasks.",
      receiveWeekends: "Receive on weekends too",
      saved: "Settings saved",
      saveError: "Error saving",
    },
    absence: {
      title: "Vacation / absence",
      description: "You won't receive email notifications during absences",
      from: "From",
      to: "To",
      reason: "Reason (optional)",
      reasonPlaceholder: "e.g. Vacation, Sick leave",
      add: "Add absence",
      added: "Absence added",
      removed: "Absence removed",
      dateError: "Start date must be before end date",
      saveError: "Error saving",
      deleteError: "Error deleting",
    },
    workHours: {
      sectionTitle: "Your work hours",
      title: "Work hours",
      description: "Set the start and end of your work day. The alignment loop, middle-column statuses, and the time your morning + evening emails fire will all follow.",
      startLabel: "Start (morning email)",
      endLabel: "End (evening email)",
      timezoneLabel: "Timezone",
      timezoneHint: "Detected from your browser",
      detectTz: "Detect",
      save: "Save",
      saved: "Work hours updated",
      loading: "Loading …",
      errorRange: "End hour must be later than start hour.",
      errorSave: "Save failed.",
    },
    billing: {
      title: "Subscription",
      freePlan: "Free",
      proPlan: "PRO",
      premiumPlan: "PREMIUM",
      statusActive: "Active",
      statusPastDue: "Payment past due",
      statusCancelled: "Cancelled",
      statusExpired: "Expired",
      statusTrial: "Trial",
      activeUntil: "Active until",
      cancelsOn: "Cancels on",
      pastDueNote: "Your latest payment failed. Update your card to keep the subscription active.",
      manage: "Manage subscription",
      invoices: "Invoices",
      cancelSub: "Cancel subscription",
      cancelConfirm: "You'll be taken to the LemonSqueezy portal to cancel. After cancelling you stay on your current plan until the end of the billing period.",
      cancelTrial: "Cancel trial",
      cancelTrialConfirm: "Are you sure you want to cancel your free trial? Your plan will switch to free immediately and you'll lose access to paid AI features.",
      cancelTrialDone: "Trial cancelled. Your plan is now free.",
      noSubHint: "Your plan was assigned manually (no real subscription). Start a paid plan above to access invoices and management.",
      changePlan: "Change plan",
      upgradeCta: "Upgrade",
      switchCta: "Switch to",
      currentPlanBadge: "Your plan",
      vatIncluded: "VAT included",
      paymentFailedHelp: "Update payment method",
      loadingStatus: "Loading subscription status…",
      successToast: "Subscription activated successfully.",
      cycleMonthly: "monthly",
      cycleYearly: "yearly",
    },
    changePassword: {
      sectionTitle: "Password",
      title: "Change password",
      description: "Update the password you use to sign in to your account.",
      current: "Current password",
      currentPlaceholder: "Current password",
      new: "New password",
      newPlaceholder: "At least 8 characters",
      confirm: "Repeat new password",
      confirmPlaceholder: "Repeat new password",
      submit: "Change password",
      saving: "Saving…",
      saved: "Password updated.",
      errorCurrent: "Current password is incorrect.",
      errorMismatch: "New passwords don't match.",
      errorShort: "Password must be at least 8 characters.",
      errorGeneric: "Change failed. Please try again.",
      noPasswordHint: "You sign in with Google and don't have a password set yet. Use “Forgot password?” on the login page to set one.",
    },
    dataPrivacy: {
      sectionTitle: "Data & privacy",
      exportTitle: "Export your data",
      exportDescription: "Download all of your data — profile, journal entries, goals, tasks, conversations and settings — as a single JSON bundle.",
      exportButton: "Download all (JSON)",
      exporting: "Preparing…",
      exportError: "Export failed. Please try again.",
    },
    deleteAccount: {
      sectionTitle: "Danger zone",
      title: "Delete account",
      description: "Permanently deletes your account and all data — journal entries, goals, tasks, conversations and settings.",
      warning: "This action cannot be undone.",
      exportHint: "You can export your data in the section above before deleting.",
      button: "Delete account",
      confirmPhrase: "DELETE",
      confirmPrompt: "Type DELETE to confirm",
      passwordLabel: "Confirm with your password",
      passwordPlaceholder: "Your password",
      confirmButton: "Permanently delete account",
      cancel: "Cancel",
      activeSubError: "Cancel your subscription in the Billing section first, then delete your account.",
      invalidPassword: "Incorrect password.",
      genericError: "Deletion failed. Please try again.",
      deleting: "Deleting…",
    },
  },
  support: {
    title: "Help & suggestions",
    newTicket: "New message",
    myMessages: "My messages",
    subject: "Subject",
    subjectPlaceholder: "Brief description of issue or suggestion",
    message: "Message",
    messagePlaceholder: "Describe details...",
    send: "Send",
    sent: "Message sent!",
    sendError: "Error sending message.",
    replyError: "Error sending reply.",
    noMessages: "No messages",
    createNew: "Create a new issue for help.",
    open: "Open",
    resolved: "Resolved",
    markResolved: "Mark as resolved",
    reopen: "Reopen",
    statusChanged: "Status changed.",
    statusError: "Error changing status.",
    writeReply: "Write a reply...",
    floatingButton: "Have an issue or suggestion? Write to us!",
    user: "User",
    adminLabel: "Admin",
  },
  notifications: {
    title: "Notifications",
    markAllRead: "Mark all as read",
    noNotifications: "No notifications",
  },
  emails: {
    journalEntry: "Journal entry",
    morningPlan: "Today's plan",
    eveningReminder: "Evening reminder",
    openJournal: "Open journal",
    finishJournal: "Finish journal",
    startJournal: "Start journal",
    completedTasks: "Completed tasks",
    overdue: "Overdue",
    today: "Today",
    tasksForToday: "Tasks for today",
    noOpenTasks: "No open tasks for today.",
    autoGenerated: "Automatically generated by Journail",
    important: "Important",
    entryExists: "You've already created a journal entry today. Time to wrap up!",
    noEntry: "You don't have a journal entry today. Here are your tasks:",
    newUserRegistered: "New user registered",
    accountApproved: "Your account is approved!",
    accountApprovedMessage: "Your Journail account has been approved. You can now log in and start using the app.",
    loginNow: "Sign in",
    signoffMorning: "Have a good one,\n— Journail",
    signoffEvening: "Sleep well,\n— Journail",
    signoffJournal: "Thoughts on paper, a clear head. Have a lovely day.\n— Journail",
    plan: "Plan",
    summary: "Summary",
    eyebrowMorning: "Morning brief",
    eyebrowEvening: "Evening reminder",
    eyebrowJournal: "Journal entry",
    eyebrowApproval: "Your account",
    eyebrowAdmin: "Admin",
    eyebrowReset: "Password reset",
    passwordResetTitle: "Reset your password",
    passwordResetMessage: "We received a request to reset the password for your Journail account. Click the button below to set a new password.",
    passwordResetExpiry: "This link is valid for 1 hour.",
    passwordResetIgnore: "If you didn't request this, you can safely ignore this email — your password stays unchanged.",
    resetCta: "Reset password",
    adminPendingHeading: "New user awaiting approval",
    adminPendingNameLabel: "Name",
    adminPendingDateLabel: "Date",
    adminOpenPanel: "Open admin panel",
    eyebrowTrial: "Your trial",
    trialStartedTitle: "Your 7-day trial is live.",
    trialStartedMessage: "Welcome to Journail. For the next 7 days you have full Pro access — the AI Assistant, planning, and journal. No credit card required. Start with the first loop of your day.",
    trialOpenCta: "Open Journail",
    trialReminderTitle: "Your trial ends soon.",
    trialReminderDays: "Time left in your trial: {n}.",
    trialReminderMessage: "To keep the AI Assistant, planning, and journal after your trial, choose a plan. No interruption — all your content stays exactly where it is.",
    trialReminderCta: "Choose a plan",
    trialEndedTitle: "Your trial has ended.",
    trialEndedMessage: "Your 7-day trial has run out. Your content is safely stored. To reactivate the AI Assistant and planning, start a subscription — everything picks up right where you left off.",
    trialEndedCta: "Activate subscription",
    adminSignupHeading: "New trial signup",
    footerNote: "Sent automatically. You can reply to this email directly.",
    unsubscribeNote: "You can turn off email reminders in settings.",
    settingsLink: "Email settings",
    settingsLinkLabel: "Change in settings",
  },
  consent: {
    banner: {
      title: "A note about cookies",
      description: "We set a few cookies that are required to keep you logged in and the Service working. With your permission, we'd also like to use analytics (Google Analytics, advertising features off) so we can see what to improve. No advertising. Your journal entries are never sent to analytics.",
      acceptAll: "Accept all",
      rejectAll: "Reject all",
      customize: "Customise",
      saveSelection: "Save preferences",
      noticeTitle: "A note about cookies",
      noticeDescription: "We set a few cookies to keep you logged in. We do not use marketing trackers. You can manage analytics below or use the \"Do Not Sell or Share My Personal Information\" link in the footer.",
      doNotSell: "Do Not Sell or Share My Personal Information",
    },
    categories: {
      necessary: {
        title: "Strictly necessary",
        description: "Required for the Service to work. Keeps you logged in, protects against attacks, remembers your language choice. Cannot be turned off.",
      },
      functional: {
        title: "Functional",
        description: "Remembers preferences like dashboard layout. Improves convenience but the Service works without them.",
      },
      analytics: {
        title: "Analytics",
        description: "Google Analytics and PostHog (EU), so we can see what to improve. Advertising features are off, IP addresses aren't stored, and your journal entries, goals, and AI conversations are never sent to analytics.",
      },
      marketing: {
        title: "Marketing",
        description: "We don't use marketing cookies, and there are no third-party advertising trackers on journail.app.",
      },
    },
    settings: {
      title: "Cookies & privacy",
      subtitle: "Manage your consent for cookies and marketing technologies.",
      manage: "Manage settings",
      currentChoices: "Current selection",
      lastUpdated: "Last updated",
      privacyLink: "Read the privacy policy",
    },
    admin: {
      title: "Consent",
      subtitle: "Audit log of consent events (proof-of-consent).",
      version: "Active policy version",
      filterRegion: "Region",
      filterAll: "All",
      time: "Time",
      subject: "Subject",
      user: "User",
      region: "Region",
      country: "Country",
      event: "Event",
      categories: "Categories",
      gpc: "GPC",
      empty: "No records yet.",
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
      preferences: "Cookie settings",
    },
  },
  composer: {
    modeSwitch: {
      eyebrow: "// MODE CHANGE",
      titleTo: "Switch to {toLabel} mode?",
      bodyFromTo: "AI's voice will shift from {fromLabel} to {toLabel}. Your context stays.",
      cancelKeep: "Keep {fromLabel}",
      confirm: "Switch",
      modePlan: "Plan",
      modeReflect: "Reflect",
      modeAsk: "Ask",
    },
    slash: {
      taskAdd: "Add a task to today's list",
      taskEdit: "Edit a task (search → pick)",
      taskComplete: "Complete a task (search → pick)",
      taskDelete: "Delete a task (search → pick)",
      taskMove: "Move a task (search → pick date)",
      taskCompleteSoon: "Picker coming soon",
      journalSearch: "Search journal entries",
      note: "Add a note (AI won't reply; folded into the day's debrief)",
      noteAdded: "Note added",
      noteUnavailable: "Notes are available during the day — after the plan is accepted.",
      today: "Jump to today",
      yesterday: "Open yesterday's entry",
      yesterdayMissing: "No entry for yesterday yet.",
      help: "Show all commands",
      helpToast: "Commands: /task /journal /today /yesterday /tomorrow /help",
      planTomorrow: "Open tomorrow's plan",
      sectionTasks: "Tasks",
      sectionJournal: "Journal",
      sectionQuick: "Quick",
      unknown: "Unknown command",
    },
    undo: {
      taskAdded: "Added '{content}' for today.",
      label: "Undo",
    },
    ui: {
      contextAttached: "CONTEXT ATTACHED",
      noContextYet: "NO CONTEXT YET",
      quickReply: "Quick reply",
      quickReplies: "Quick replies",
      orWriteYourOwn: "or write your own",
      send: "send",
      newline: "newline",
      commands: "commands",
      stop: "Stop",
      thinking: "Journail is thinking…",
      navigate: "navigate",
      select: "select",
      complete: "complete",
      cancel: "cancel",
    },
  },
  onboarding: {
    ariaLabel: "Onboarding guide",
    eyebrow: "GET STARTED",
    heading: { plain: "Six", em: "small", suffix: " steps." },
    subtitle: "Once they're done, the journal comes to life.",
    dismiss: "Hide",
    stepLabel: "STEP {n} OF {total}",
    showAll: "Show all {total} steps",
    hideAll: "Collapse",
    currentBadge: "CURRENT",
    markDone: "Mark as done",
    markNotDone: "Mark as not done",
    allDoneHeading: { em: "You're", suffix: " ready." },
    steps: {
      setLanguages: {
        title: "Pick your languages",
        cta: "Settings",
        hint: "In Settings → Language pick your UI language (Slovenian or English) and your content language — the one the assistant replies in and you write your journal in. Over 80 languages available.",
      },
      firstGoal: {
        title: "Define your first goal",
        cta: "Open goals",
        hint: "Head to Goals and add your first one. You can also dictate a goal to the AI Assistant below in plain language anytime (e.g. \"add goal: run 5 km by summer\").",
      },
      calendar: {
        title: "Connect your calendar",
        cta: "Settings",
        hint: "In Settings → Integrations you can connect your Google Calendar. Journail will automatically pull in your events as context for planning your day.",
      },
      taskSource: {
        title: "Pick a task source",
        cta: "Open tasks",
        hint: "Create your first task on the Tasks page. If you prefer, connect your Todoist in Settings and use it instead of the internal system. Remember you can also just tell the AI Assistant — say \"add task …\".",
      },
      sideChat: {
        title: "Try the assistant",
        cta: "Ask",
        hint: "In the AI Assistant below ask anything — e.g. \"what's still on my plate today\" or \"in which journals did I write about Journail\".",
      },
      firstPlanning: {
        title: "Run your first plan",
        cta: "Start",
        hint: "Open the journal and start today's plan. The assistant walks you through priorities, events and goals so you shape today's plan together.",
      },
      firstEntry: {
        title: "Close your first day",
        cta: "Go",
        hint: "In the evening — or once you wrap up work — open the journal, capture what you got done together with the assistant, and press \"Finish day\". That's your first journal page!",
      },
    },
  },
  trialGate: {
    title: "Your free trial has ended",
    subtitle: "Upgrade to Pro or Premium to keep going. Your journals, goals and tasks stay safely saved.",
    signOut: "Sign out",
    publicSite: "Back to public site",
  },
};
